import 'react-day-picker/dist/style.css';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import type { DateRange } from 'react-day-picker';
import { DEFAULT_DATE_RANGE_MIN_DATE } from '@/shared/config';
import { useDateRange } from '@/shared/lib';
import { Button } from '@/shared/components/ui';
import { ModalComponentWrapper } from '@/shared/components/ModalComponentWrapper';
import { RangePicker } from '@/shared/components/RangePicker';
import { CalendarHeader } from './CalendarHeader';

type Props = {
  onApply: (dateFrom?: Date, dateTo?: Date) => void;
  initDate?: Date;
};

export function RangeDateModal({ onApply, initDate }: Props) {
  const { t } = useTranslation();
  const maxDate = useMemo(() => new Date(), []);

  const {
    locale,
    limits: { minDate },
    range: { selectedRange, setSelectedRange },
    calendar: { currentMonth, setCurrentMonth },
    view: { handleMonthChange },
    canNavigate,
  } = useDateRange({ minDate: DEFAULT_DATE_RANGE_MIN_DATE, maxDate, initDate });

  const handleMonthSelect = (monthIndex: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), monthIndex, 1));
  };

  const handleYearSelect = (year: number) => {
    const candidate = new Date(year, currentMonth.getMonth(), 1);
    if (candidate > maxDate) {
      setCurrentMonth(new Date(year, maxDate.getMonth(), 1));
    } else if (candidate < minDate) {
      setCurrentMonth(new Date(year, minDate.getMonth(), 1));
    } else {
      setCurrentMonth(candidate);
    }
  };

  const handleRangeSelect = (range: DateRange | undefined) => {
    setSelectedRange(range);
  };

  const applyText = useMemo(() => {
    const from = selectedRange?.from;
    const to = selectedRange?.to;

    if (!from) return t('dateRangeModal.rangePlaceholder');

    const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
    const dd = (d: Date) => format(d, 'dd');
    const shortMonth = (d: Date) =>
      cap(format(d, 'MMM', { locale }).replace('.', ''));
    const fullMonth = (d: Date) => cap(format(d, 'MMMM', { locale }));
    const yyyy = (d: Date) => format(d, 'yyyy');

    if (!to) {
      return t('dateRangeModal.rangeApplyButtonSingle', {
        date: `${dd(from)} ${fullMonth(from)} ${yyyy(from)}`,
      });
    }

    return t('dateRangeModal.rangeApplyButtonRange', {
      from: `${dd(from)} ${shortMonth(from)} ${yyyy(from)}`,
      to: `${dd(to)} ${shortMonth(to)} ${yyyy(to)}`,
    });
  }, [selectedRange, locale, t]);

  const handleApply = () => {
    const from = selectedRange?.from;
    if (!from) return;
    onApply(from, selectedRange?.to ?? from);
  };

  return (
    <ModalComponentWrapper
      title="dateRangeModal.rangeTitle"
      className="rounded-[3.2rem]"
      contentClassName="gap-[2.4rem] sm:gap-[2.4rem]">
      <CalendarHeader
        currentMonth={currentMonth}
        locale={locale}
        minDate={minDate}
        maxDate={maxDate}
        canNavigate={canNavigate}
        onMonthSelect={handleMonthSelect}
        onYearSelect={handleYearSelect}
        onMonthChange={handleMonthChange}
      />

      <RangePicker
        selected={selectedRange}
        onSelect={handleRangeSelect}
        month={currentMonth}
        onMonthChange={setCurrentMonth}
        locale={locale}
        disabled={(date: Date) => date < minDate || date > maxDate}
      />

      <Button
        size="lg"
        disabled={!selectedRange?.from}
        onClick={handleApply}
        className="disabled:bg-muted disabled:text-secondary disabled:opacity-100">
        {applyText}
      </Button>
    </ModalComponentWrapper>
  );
}
